import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, inject, NgModule, ViewEncapsulation } from '@angular/core';
import { ValidateMessageComponent } from '../validate-message/validate-message.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GtmClickDirective, InputValidatorDirective, InvalidControlScrollDirective } from '../../directives';
import { ToastrService } from 'ngx-toastr';
import { NgxsModule, select, Store } from '@ngxs/store';
import { NewsletterState, NewsletterStateSubmit } from './newsletter.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { INewsletterSaveDto } from '../../models';
import { Angulartics2GoogleTagManager } from 'angulartics2';

export type NewsLetterFormType = {
  firstname: FormControl<string | null>;
  email: FormControl<string | null>;
  barber: FormControl<1 | 0 | null>;
  agreementEmail: FormControl<boolean | null>;
};

@NgModule({
  imports: [NgxsModule.forFeature([NewsletterState])],
})
class NewsletterStateLazyModule {}

@Component({
  selector: 'app-newsletter-section',
  templateUrl: './newsletter.component.html',
  styleUrls: ['./newsletter.component.scss'],
  imports: [
    CommonModule,
    NgOptimizedImage,
    ValidateMessageComponent,
    InputValidatorDirective,
    ReactiveFormsModule,
    InvalidControlScrollDirective,
    NewsletterStateLazyModule,
    GtmClickDirective,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class NewsletterComponent {
  private readonly _store: Store = inject(Store);
  public readonly loading = select(NewsletterState.loading);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  public readonly form: FormGroup<NewsLetterFormType> = this._formBuilder.group({
    firstname: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email, Validators.maxLength(255)]),
    barber: new FormControl<1 | 0 | null>(null, [Validators.required]),
    agreementEmail: new FormControl<boolean | null>(false, [Validators.requiredTrue]),
  });
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);

  constructor(
    private readonly _toastrService: ToastrService,
    private readonly _destroyRef: DestroyRef,
  ) {}

  get f() {
    return this.form.controls;
  }

  public onSave(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (!this.form.valid) {
      this._toastrService.error('Proszę poprawnie wypełnić formularz.', 'Błąd');
      return;
    }

    if (
      !this.f.firstname.value ||
      !this.f.email.value ||
      this.f.barber.value === null ||
      this.f.barber.value === undefined ||
      !this.f.agreementEmail.value
    ) {
      throw new Error('Form is not valid');
    }
    const dto: INewsletterSaveDto = {
      firstname: this.f.firstname.value,
      email: this.f.email.value,
      barber: this.f.barber.value,
      agreementEmail: 1,
    };

    this._store
      .dispatch(new NewsletterStateSubmit({ dto }))
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          this._toastrService.error('Wystąpił błąd podczas wysyłania formularza. Proszę spróbować ponownie.', 'Błąd');
          return throwError(() => error);
        }),
        tap(() => {
          this._toastrService.success('Dziękujemy za zapisanie się do newslettera.', 'Sukces');
          this.form.reset();
          Object.keys(this.form.controls).forEach((key) => {
            // @ts-ignore
            this.form.get(key).markAsUntouched();
          });

          this.pushLayer('my account');
        }),
      )
      .subscribe();
  }

  public pushLayer(referral: string): void {
    this._angulartics2GoogleTagManager.pushLayer({
      event: 'uaevent',
      ecommerce: 'undefined',
      eventCategory: 'registration',
      eventAction: 'newsletter',
      eventLabel: referral,
      event_name: 'newsletter_registration',
      referral: referral,
    });
  }
}
